import { IconChevronDown, IconChevronUp } from "@/assets/icons";
import { IconGoogle } from "@/assets/icons/IconGoogle";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { registerSchema } from "@/features/auth/schema/registerSchema";
import { registerApi } from "@/features/auth/services/registerApi";
import { TRegisterForm } from "@/features/auth/types/registerForm";
import { TGoogleRegisterState } from "@/features/auth/types/google-register";
import { TRegisterQueryParam } from "@/features/auth/types/registerQueryParam";
import { buildCognitoGoogleLoginUrl } from "@/features/auth/utils/buildCognitoGoogleLoginUrl";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { showError } from "@/utils/error";
import { omit } from "@/utils/object";
import { getQueryFromURL } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Collapse, Stack, styled, Typography } from "@mui/material";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

const initValue: TRegisterForm = {
  email: "",
  password: "",
  confirmPassword: "",
};

export const RegisterForm = () => {
  const navigate = useNavigate();

  const query = getQueryFromURL<TRegisterQueryParam>();

  const [showEmailLogin, setShowEmailLogin] = useState(false);

  const methods = useForm<TRegisterForm>({
    defaultValues: { ...initValue, ...query },
    resolver: yupResolver(registerSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { control } = methods;

  const onSubmit = async (data: TRegisterForm) => {
    if (!query) return null;
    try {
      await registerApi({ ...omit(data, "confirmPassword"), resetToken: query?.resetToken });

      navigate(AppRoutes.registerSuccess, {
        replace: true,
        state: {
          email: data?.email,
        },
      });
    } catch (error) {
      showError(error);
    }
  };

  const handleGoogleLogin = () => {
    if (!query?.email || !query?.resetToken) return;

    const state: TGoogleRegisterState = { email: query.email, resetToken: query.resetToken };
    window.location.href = buildCognitoGoogleLoginUrl(JSON.stringify(state));
  };

  return (
    <Form methods={methods} onSubmit={onSubmit} enableBlocker={false} schema={registerSchema}>
      <Stack gap={4}>
        <Stack gap={2}>
          <Stack flexDirection="column" gap="12px">
            <FormField control={control} name="email">
              <Input size="lg" readonly />
            </FormField>
            <GoogleButton fullWidth onClick={handleGoogleLogin}>
              <IconGoogle />
              <Typography variant="body14Semi">Google Workspace連携登録</Typography>
            </GoogleButton>
          </Stack>
          <Box display="flex" alignItems="center" gap={2} my={2}>
            <Box flex={1} height="1px" bgcolor="#E0E0E0" />
            <Typography color={text.secondary} fontSize="14px">
              または
            </Typography>
            <Box flex={1} height="1px" bgcolor="#E0E0E0" />
          </Box>
          <CollapsibleHeader onClick={() => setShowEmailLogin(!showEmailLogin)}>
            <Typography color={text.secondary} fontSize="14px" fontWeight={500}>
              メールアドレスで登録
            </Typography>
            {showEmailLogin ? (
              <IconChevronUp style={{ color: text.secondary, fontSize: "18px" }} />
            ) : (
              <IconChevronDown style={{ color: text.secondary, fontSize: "18px" }} />
            )}
          </CollapsibleHeader>
          <Collapse in={showEmailLogin}>
            <FormField control={control} name="password" sx={{ mb: "24px" }}>
              <Input size="lg" type="password" />
            </FormField>
            <FormField control={control} name="confirmPassword" sx={{ mb: "12px" }}>
              <Input size="lg" type="password" />
            </FormField>
            <Typography variant="body14" color={text.secondary} mt={1}>
              ※パスワードはアルファベット大文字・小文字・数字・記号を含む12文字以上で入力してください。
            </Typography>
            <Button
              size="sm"
              type="submit"
              variant="outline"
              fullWidth
              sx={{
                mt: "32px",
                width: "100%",
              }}
            >
              <Typography variant="body14Semi">Google連携せずにアカウントを登録する</Typography>
            </Button>
          </Collapse>
        </Stack>
      </Stack>
    </Form>
  );
};

const GoogleButton = styled(Button)`
  &.MuiButtonBase-root {
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    height: 60px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
  }
`;

const CollapsibleHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px;
  &:hover {
    opacity: 0.8;
  }
`;
